import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import Button from './ui/Button';
import AutocompleteInput from './ui/AutocompleteInput';
import { PropertyFilters, PropertyPurpose } from '../types/property';
import { neighborhoodOptions } from '../lib/propertySearch';

interface FilterModalProps {
  isOpen: boolean;
  initialFilters: PropertyFilters;
  onClose: () => void;
  onApply: (filters: PropertyFilters) => void;
}

const transactionOptions: Array<{ value: 'venda' | 'aluguel'; label: string }> = [
  { value: 'venda', label: 'Comprar' },
  { value: 'aluguel', label: 'Alugar' },
];

const purposeOptions: Array<{ value: PropertyPurpose; label: string }> = [
  { value: 'residencial', label: 'Residencial' },
  { value: 'comercial', label: 'Comercial' },
];

const typeOptionsMulti: Array<{ value: string; label: string }> = [
  { value: 'apartamento', label: 'Apartamento' },
  { value: 'casa', label: 'Casa' },
  { value: 'casa-condominio', label: 'Casa em Condomínio' },
  { value: 'cobertura', label: 'Cobertura' },
  { value: 'comercial', label: 'Comercial' },
];

const stepValues = [1, 2, 3, 4, 5];

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
}

function parseCurrencyInput(raw: string): number | undefined {
  if (!raw) return undefined;
  const digits = raw.replace(/\D/g, '');
  if (!digits) return undefined;
  const parsed = Number(digits);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

export default function FilterModal({ isOpen, initialFilters, onClose, onApply }: FilterModalProps) {
  const [draft, setDraft] = useState<PropertyFilters>(initialFilters);
  const [priceMinInput, setPriceMinInput] = useState<string>(
    initialFilters.priceMin ? formatCurrency(initialFilters.priceMin) : ''
  );
  const [priceMaxInput, setPriceMaxInput] = useState<string>(
    initialFilters.priceMax ? formatCurrency(initialFilters.priceMax) : ''
  );

  useEffect(() => {
    if (isOpen) {
      setDraft(initialFilters);
      setPriceMinInput(initialFilters.priceMin ? formatCurrency(initialFilters.priceMin) : '');
      setPriceMaxInput(initialFilters.priceMax ? formatCurrency(initialFilters.priceMax) : '');
    }
  }, [isOpen, initialFilters]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  const neighborhoodSuggestions = useMemo(
    () => neighborhoodOptions.slice(1).map((option) => option.label),
    []
  );

  const quickNeighborhoods = useMemo(() => neighborhoodSuggestions.slice(0, 8), [neighborhoodSuggestions]);

  if (!isOpen) return null;

  const selectedTypes = draft.types || [];

  const toggleType = (value: string) => {
    const exists = selectedTypes.includes(value);
    const next = exists ? selectedTypes.filter((t) => t !== value) : [...selectedTypes, value];
    setDraft({ ...draft, types: next.length > 0 ? next : undefined });
  };

  const setStep = (key: 'bedrooms' | 'suites' | 'parkingSpaces', value: number) => {
    setDraft({ ...draft, [key]: draft[key] === value ? undefined : value });
  };

  const handleApply = () => {
    onApply({
      ...draft,
      priceMin: parseCurrencyInput(priceMinInput),
      priceMax: parseCurrencyInput(priceMaxInput),
    });
  };

  const handleClear = () => {
    setDraft({});
    setPriceMinInput('');
    setPriceMaxInput('');
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="relative flex w-full max-w-3xl max-h-[90vh] flex-col rounded-lg bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-xl font-serif text-forest-900">Todos os filtros</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition-colors hover:bg-gray-100"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <section className="mb-8">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Em qual localização?
            </h3>
            <AutocompleteInput
              placeholder="Digite o bairro..."
              value={draft.neighborhood || ''}
              options={neighborhoodSuggestions}
              onChange={(value) => setDraft({ ...draft, neighborhood: value || undefined })}
            />
            {quickNeighborhoods.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {quickNeighborhoods.map((label) => {
                  const isActive = draft.neighborhood === label;
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() =>
                        setDraft({ ...draft, neighborhood: isActive ? undefined : label })
                      }
                      className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
                        isActive
                          ? 'border-forest-900 bg-forest-900 text-white'
                          : 'border-gray-300 text-gray-700 hover:border-forest-900 hover:text-forest-900'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className="mb-8">
            <h3 className="mb-4 text-lg font-medium text-forest-900">Tipo de negócio</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-sm font-medium text-gray-700">Transação</p>
                <div className="flex flex-wrap gap-2">
                  {transactionOptions.map((option) => {
                    const isActive = draft.transaction === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() =>
                          setDraft({ ...draft, transaction: isActive ? undefined : option.value })
                        }
                        className={`rounded-md border px-5 py-2 text-sm transition-colors ${
                          isActive
                            ? 'border-forest-900 bg-forest-900 text-white'
                            : 'border-gray-300 text-gray-700 hover:border-forest-900'
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium text-gray-700">Finalidade</p>
                <div className="flex flex-wrap gap-2">
                  {purposeOptions.map((option) => {
                    const isActive = draft.purpose === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() =>
                          setDraft({ ...draft, purpose: isActive ? undefined : option.value })
                        }
                        className={`rounded-md border px-5 py-2 text-sm transition-colors ${
                          isActive
                            ? 'border-forest-900 bg-forest-900 text-white'
                            : 'border-gray-300 text-gray-700 hover:border-forest-900'
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h3 className="mb-4 text-lg font-medium text-forest-900">Tipo de imóvel</h3>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {typeOptionsMulti.map((option) => {
                const isActive = selectedTypes.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggleType(option.value)}
                    className={`rounded-md border px-4 py-2.5 text-sm transition-colors ${
                      isActive
                        ? 'border-forest-900 bg-forest-900 text-white'
                        : 'border-gray-300 text-gray-700 hover:border-forest-900'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="mb-8">
            <h3 className="mb-4 text-lg font-medium text-forest-900">Características</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <StepperGroup
                label="Dormitórios"
                helper="a partir de"
                value={draft.bedrooms}
                onChange={(v) => setStep('bedrooms', v)}
              />
              <StepperGroup
                label="Suítes"
                helper="a partir de"
                value={draft.suites}
                onChange={(v) => setStep('suites', v)}
              />
              <StepperGroup
                label="Vagas"
                helper="a partir de"
                value={draft.parkingSpaces}
                onChange={(v) => setStep('parkingSpaces', v)}
              />
            </div>
          </section>

          <section className="mb-4">
            <h3 className="mb-4 text-lg font-medium text-forest-900">Faixa de valor</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Valor mínimo
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={priceMinInput}
                  onChange={(e) => {
                    const parsed = parseCurrencyInput(e.target.value);
                    setPriceMinInput(parsed ? formatCurrency(parsed) : '');
                  }}
                  placeholder="Sem mínimo"
                  className="w-full rounded-md border border-gray-300 px-4 py-2.5 text-sm focus:border-forest-900 focus:outline-none focus:ring-1 focus:ring-forest-900"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Valor máximo
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={priceMaxInput}
                  onChange={(e) => {
                    const parsed = parseCurrencyInput(e.target.value);
                    setPriceMaxInput(parsed ? formatCurrency(parsed) : '');
                  }}
                  placeholder="Sem máximo"
                  className="w-full rounded-md border border-gray-300 px-4 py-2.5 text-sm focus:border-forest-900 focus:outline-none focus:ring-1 focus:ring-forest-900"
                />
              </div>
            </div>
          </section>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <Button variant="outline" onClick={handleClear}>
            Limpar filtros
          </Button>
          <Button onClick={handleApply}>Buscar</Button>
        </div>
      </div>
    </div>
  );
}

interface StepperGroupProps {
  label: string;
  helper?: string;
  value?: number;
  onChange: (value: number) => void;
}

function StepperGroup({ label, helper, value, onChange }: StepperGroupProps) {
  return (
    <div>
      <p className="mb-2 text-sm font-medium text-gray-700">
        {label} {helper && <span className="text-xs font-normal text-gray-400">({helper})</span>}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {stepValues.map((n) => {
          const isActive = value === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className={`h-10 w-10 rounded-md border text-sm transition-colors ${
                isActive
                  ? 'border-forest-900 bg-forest-900 text-white'
                  : 'border-gray-300 text-gray-700 hover:border-forest-900'
              }`}
            >
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}
